import { useState, useEffect } from 'react';
import { FormInput } from '../ui/FormInput';
import { calculateTotalParticipants } from '../../utils/calculations';
import { Report } from '../../types/report';

interface AttendanceSectionProps {
  onChange: (data: any) => void;
  initialData?: Partial<Report>;
}

function AttendanceSection({ onChange, initialData }: AttendanceSectionProps) {
  const [attendance, setAttendance] = useState<Report['attendance']>({
    adults: { men: 0, women: 0 },
    children: { boys: 0, girls: 0 },
    served: { men: 0, women: 0 },
    blooms: 0,
    conversions: { men: 0, women: 0 }
  });

  const [newMembers, setNewMembers] = useState<Report['newMembers']>({
    men: 0,
    women: 0,
    blooms: 0,
    children: 0
  });

  useEffect(() => {
    if (initialData?.attendance) {
      setAttendance(initialData.attendance);
    }
    if (initialData?.newMembers) {
      setNewMembers(initialData.newMembers);
    }
  }, [initialData]);

  const handleAttendanceChange = (category: string, subCategory: string, value: string) => {
    const numValue = parseInt(value) || 0;
    const categoryValue = attendance[category as keyof typeof attendance];
    const newAttendance = {
      ...attendance,
      [category]: typeof categoryValue === 'object' && categoryValue !== null
        ? {
            ...(categoryValue as any),
            [subCategory]: numValue
          }
        : numValue
    };
    setAttendance(newAttendance);
    onChange({ attendance: newAttendance, newMembers });
  };

  const handleNewMembersChange = (field: keyof Report['newMembers'], value: string) => {
    const numValue = parseInt(value) || 0;
    const updatedNewMembers = { ...newMembers, [field]: numValue };
    setNewMembers(updatedNewMembers);
    onChange({ attendance, newMembers: updatedNewMembers });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h2 className="text-lg font-semibold text-gray-800 mb-6">Effectifs des participants</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Adultes */}
        <div>
          <h3 className="font-medium text-gray-700 mb-4">Adultes</h3>
          <div className="space-y-4">
            <FormInput
              type="number"
              min="0"
              label="Hommes"
              value={attendance.adults?.men || 0}
              onChange={(e) => handleAttendanceChange('adults', 'men', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Femmes"
              value={attendance.adults?.women || 0}
              onChange={(e) => handleAttendanceChange('adults', 'women', e.target.value)}
            />
          </div>
        </div>

        {/* Enfants */}
        <div>
          <h3 className="font-medium text-gray-700 mb-4">Enfants</h3>
          <div className="space-y-4">
            <FormInput
              type="number"
              min="0"
              label="Garçons"
              value={attendance.children?.boys || 0}
              onChange={(e) => handleAttendanceChange('children', 'boys', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Filles"
              value={attendance.children?.girls || 0}
              onChange={(e) => handleAttendanceChange('children', 'girls', e.target.value)}
            />
          </div>
        </div>

        {/* Boss ayant servi */}
        <div>
          <h3 className="font-medium text-gray-700 mb-4">Boss ayant servi</h3>
          <div className="space-y-4">
            <FormInput
              type="number"
              min="0"
              label="Hommes"
              value={attendance.served?.men || 0}
              onChange={(e) => handleAttendanceChange('served', 'men', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Femmes"
              value={attendance.served?.women || 0}
              onChange={(e) => handleAttendanceChange('served', 'women', e.target.value)}
            />
          </div>
        </div>

        {/* Blooms */}
        <div className="md:col-span-3">
          <h3 className="font-medium text-gray-700 mb-4">Blooms</h3>
          <FormInput
            type="number"
            min="0"
            label="Nombre"
            value={attendance.blooms || 0}
            onChange={(e) => handleAttendanceChange('blooms', '', e.target.value)}
          />
        </div>

        {/* Appel à conversion */}
        <div className="md:col-span-3">
          <h3 className="font-medium text-gray-700 mb-4">Appel à conversion</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              type="number"
              min="0"
              label="Hommes"
              value={attendance.conversions?.men || 0}
              onChange={(e) => handleAttendanceChange('conversions', 'men', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Femmes"
              value={attendance.conversions?.women || 0}
              onChange={(e) => handleAttendanceChange('conversions', 'women', e.target.value)}
            />
          </div>
        </div>

        {/* Nouveaux reçus */}
        <div className="md:col-span-3 mt-6 pt-6 border-t border-gray-200">
          <h3 className="font-medium text-gray-700 mb-4">Nouveaux reçus</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <FormInput
              type="number"
              min="0"
              label="Hommes"
              value={newMembers.men}
              onChange={(e) => handleNewMembersChange('men', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Femmes"
              value={newMembers.women}
              onChange={(e) => handleNewMembersChange('women', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Blooms"
              value={newMembers.blooms}
              onChange={(e) => handleNewMembersChange('blooms', e.target.value)}
            />
            <FormInput
              type="number"
              min="0"
              label="Enfants"
              value={newMembers.children}
              onChange={(e) => handleNewMembersChange('children', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-sm font-medium text-gray-600">
          Total des participants: <span className="text-lg font-bold text-[#00665C]">
            {calculateTotalParticipants({ attendance: attendance })}
          </span>
        </p>
      </div>
    </div>
  );
}

export default AttendanceSection;